import React, { useContext, useState, useEffect, useRef } from 'react';
import darrow from '../toolkit/images/common/darrow.svg';
import arrow from '../toolkit/images/common/arrow.svg';
import UploadModal from './UploadModal';
import { APIContext } from '../contexts/APIContext';
import { downloadFileTemplate, fetchUploadedFileDetails, trackUserClicks } from '../utils';
import { useMsal } from '@azure/msal-react';
import { Tooltip, TooltipProvider, TooltipTrigger } from './ui/Tooltip';
import { TooltipContent } from '@radix-ui/react-tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import BounceLoader from './ui/Loaders/BounceLoader';
import { useToast } from './ui/toast/UseToast';
import { getValidToken } from '../utils';

const BulkUpload = () => {
  const { accounts } = useMsal();
  const { toast } = useToast();
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const initialFileCountRef = useRef<number>(0);
  const {
    fileDetails,
    interviewProjectNames,
    websocketConnectionRef: { current: websocket },
    userDetails,
    chatSessionId,
    WBUId,
    AppName,
    uploadedStatus,
  } = useContext(APIContext);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const hasInProgressFiles = fileDetails?.some((file: any) => file.FileStatusId === 3);

    if (hasInProgressFiles && websocket) {
      const intervalId = setInterval(async () => {
        const token = await getValidToken();

        fetchUploadedFileDetails(accounts, websocket, token);
      }, 5000);

      return () => clearInterval(intervalId);
    }
  }, [fileDetails, websocket, accounts]);

  useEffect(() => {
    if (websocket) {
      (async () => {
        const token = await getValidToken();
        fetchUploadedFileDetails(accounts, websocket, token);
      })();
    }
  }, [websocket]);

  const filteredFiles =
    fileDetails?.filter((file: any) => file.FileName?.toLowerCase().includes(searchQuery.toLowerCase())) || [];

  // Pagination
  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredFiles.slice(startIndex, endIndex);

  const handlePageChange = (page: any) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const maxVisiblePages = 6;
      let startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);
      if (endPage === totalPages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const handleEllipsisClick = () => {
    const newPage = Math.min(currentPage + 3, totalPages);
    handlePageChange(newPage);
  };

  const getStatusText = (statusId: any) => {
    switch (statusId) {
      case 1:
        return 'Error';
      case 2:
        return 'Completed';
      case 3:
        return 'In-Progress';
      default:
        return '';
    }
  };

  const getProjectName = (projectId: any) => {
    const project = interviewProjectNames.find((p: any) => p.project_id === projectId);
    return project ? project.project_name : '';
  };

  const truncateFileName = (fileName: string, maxLength = 15) => {
    if (!fileName || fileName.length <= maxLength) return fileName;
    return fileName.substring(0, maxLength) + '...';
  };

  const handleDownloadTemplate = async () => {
    const token = await getValidToken();

    trackUserClicks(websocket, 'DownloadTemplate', userDetails, chatSessionId, WBUId, AppName, accounts, token);

    downloadFileTemplate(accounts, websocket, token);
  };

  useEffect(() => {
    if (isUploading && fileDetails) {
      const currentFileCount = fileDetails.length;
      const hasNewFiles = currentFileCount > initialFileCountRef.current;
      if (hasNewFiles) {
        setIsUploading(false);
      }
      if (uploadedStatus === 'success') {
        (async () => {
          const token = await getValidToken();
          fetchUploadedFileDetails(accounts, websocket, token);
        })();
        toast({
          status: 'success',
          description: 'Transcript uploaded successfully',
        });
      } else {
        toast({
          status: 'error',
          description: 'Transcript upload failed',
        });
      }
    }
  }, [uploadedStatus]);

  return (
    <>
      <div className="c-transcript">
        <div className="c-transcript__content">
          <div className="c-transcript__downupflex">
            <div className="c-transcript__download-hint">
              <a href="javascipt:;" className="c-transcript__download-hint-link" onClick={handleDownloadTemplate}>
                Download the Template
              </a>{' '}
              and use it for bulk upload
            </div>
            <button
              className="c-transcript__upload-hint"
              data-modalid="upload-status-Modal"
              onClick={() => {
                setOpenUploadModal(true);
                (async () => {
                  const token = await getValidToken();
                  trackUserClicks(
                    websocket,
                    'UploadDocument',
                    userDetails,
                    chatSessionId,
                    WBUId,
                    AppName,
                    accounts,
                    token
                  );
                })();
              }}
            >
              <i className="icon-upload-lite"></i>
              <span>Upload</span>
            </button>
          </div>
          <div className="c-transcript__tablesection !mb-[0px]">
            <div className="c-table-section">
              <div className="c-table-responsive">
                <table className="c-config-table">
                  <caption className="sr-only">bulk upload</caption>
                  {fileDetails && fileDetails.length > 0 ? (
                    <thead>
                      <tr className="th-row">
                        <th className="th-flex" colSpan={3}>
                          <div className="c-transcript__th-rowflex">
                            <div className="c-transcript__hint-head">Recent Uploads ({filteredFiles.length})</div>
                            <div className="c-transcript__searchflex">
                              <div className="o-searchfield__wrap">
                                <div className="o-searchfield__toggle"></div>
                                <input
                                  type="text"
                                  className="o-searchfield__input"
                                  aria-label="Search"
                                  placeholder="Search By FileName..."
                                  id="searchf_1"
                                  value={searchQuery}
                                  onChange={handleSearchChange}
                                />
                              </div>{' '}
                              {/* <button className="c-transcript__tablemenu">
                              <i className="icon-more"></i>
                            </button> */}
                            </div>
                          </div>
                        </th>
                      </tr>
                      <tr>
                        <th className="!text-left" style={{ width: '20%' }}>
                          File Name
                          <a href="javascript:;" role="button" className="c-table-sort" aria-label="sort column"></a>
                        </th>
                        <th style={{ width: '60%' }}>
                          Project
                          <a href="javascript:;" role="button" className="c-table-sort" aria-label="sort column"></a>
                        </th>
                        <th style={{ width: '20%' }}>
                          Status
                          <a href="javascript:;" role="button" className="c-table-sort" aria-label="sort column"></a>
                        </th>
                      </tr>
                    </thead>
                  ) : (
                    <></>
                  )}
                  <tbody>
                    {currentItems && currentItems.length > 0 ? (
                      currentItems.map((file: any) => {
                        const status = getStatusText(file.FileStatusId);
                        const isError = file.FileStatusId === 1;

                        return (
                          <tr key={file.fileupload_id} className={isError ? '-error' : ''}>
                            <td className="flex">
                              {file.FileName && file.FileName.length > 15 ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-default">{truncateFileName(file.FileName)}</span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="bg-[#505050] border-0 max-w-[300px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2 z-50"
                                      sideOffset={5}
                                    >
                                      <p className="tooltip-content text-white text-xs not-italic break-all">
                                        {file.FileName}
                                      </p>
                                      <TooltipPrimitive.Arrow
                                        width={11}
                                        height={5}
                                        style={{
                                          fill: '#505050',
                                          zIndex: 9999,
                                        }}
                                      />
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                file.FileName
                              )}
                            </td>
                            <td>
                              {getProjectName(file.project_id) && getProjectName(file.project_id).length > 15 ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-default">
                                        {truncateFileName(getProjectName(file.project_id))}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="bg-[#505050] border-0 max-w-[300px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                                      sideOffset={5}
                                    >
                                      <p className="tooltip-content text-white text-xs not-italic break-all">
                                        {getProjectName(file.project_id)}
                                      </p>
                                      <TooltipPrimitive.Arrow width={11} height={5} style={{ fill: '#505050' }} />
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                getProjectName(file.project_id)
                              )}
                            </td>
                            <td className="-position-r">
                              {status}
                              {isError && <div className="c-transcript__error-td">{file.ERROR}</div>}
                            </td>
                          </tr>
                        );
                      })
                    ) : fileDetails && fileDetails.length > 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center py-8">
                          <p className="text-gray-500">No Files Available</p>
                        </td>
                      </tr>
                    ) : (
                      <></>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {currentItems && currentItems.length > 0 ? (
          <div className="c-pagination-container">
            {/* <!-- Dev note: Add a descriptive aria-label that corresponds to the component --> */}
            <nav role="navigation" aria-label="pagination">
              <div className="c-pagination_list">
                <div className="c-pagination_arrow">
                  {/* <!-- for disabled state, use aria-disabled="true", tabIndex={-1} --> */}
                  <a
                    href="javascript:;"
                    className="c-pagination_previous"
                    role="button"
                    aria-label="first page"
                    tabIndex={currentPage === 1 ? -1 : 0}
                    aria-disabled={currentPage === 1}
                    onClick={() => handlePageChange(1)}
                  >
                    <img src={darrow} aria-hidden="true" role="presentation" />
                  </a>
                  <a
                    href="javascript:;"
                    className="c-pagination_previous"
                    role="button"
                    aria-label="previous page"
                    tabIndex={currentPage === 1 ? -1 : 0}
                    aria-disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <img src={arrow} aria-hidden="true" role="presentation" />
                  </a>
                </div>
                <div className="c-pagination_page-item">
                  <div className="c-pagination_link" aria-live="polite">
                    {getPageNumbers().map((pageNum, index) => {
                      if (pageNum === '...') {
                        return (
                          <>
                            <div className="c-dropdown -page" data-placement="top">
                              <button type="button" role="combobox" className="c-dropdown-toggle">
                                <span
                                  key={`ellipsis-${index}`}
                                  role="button"
                                  onClick={handleEllipsisClick}
                                  aria-label="Jump 3 pages forward"
                                >
                                  {pageNum}
                                </span>
                              </button>
                            </div>
                          </>
                        );
                      }
                      return (
                        <a
                          key={pageNum}
                          href="javascript:;"
                          role="button"
                          className={`c-pagination_count ${currentPage === pageNum ? '-active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </a>
                      );
                    })}
                  </div>
                </div>
                <div className="c-pagination_arrow">
                  <a
                    href="javascript:;"
                    className="c-pagination_next"
                    role="button"
                    aria-label="previous page"
                    tabIndex={currentPage === totalPages ? -1 : 0}
                    aria-disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <img src={arrow} aria-hidden="true" role="presentation" />
                  </a>
                  <a
                    href="javascript:;"
                    className="c-pagination_next"
                    role="button"
                    aria-label="last page"
                    tabIndex={currentPage === totalPages ? -1 : 0}
                    aria-disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(totalPages)}
                  >
                    <img src={darrow} aria-hidden="true" role="presentation" />
                  </a>
                </div>
              </div>
            </nav>
          </div>
        ) : (
          <></>
        )}
      </div>
      {openUploadModal && (
        <UploadModal
          openUploadModal={openUploadModal}
          setOpenUploadModal={setOpenUploadModal}
          setIsUploading={setIsUploading}
        />
      )}
      {isUploading && (
        <div className="fixed inset-0 flex flex-col justify-center items-center bg-[#f1f1f180] backdrop-blur-[1px] z-[9999]">
          <BounceLoader loading={true} size={60} />
          <p className="mt-6 text-lg">One Moment Please...</p>
        </div>
      )}
    </>
  );
};

export default BulkUpload;
